import { Route, Switch } from 'react-router-dom';

import { TemporaryStorageHandler } from 'components/TemporaryStorageHandler';
import ErrorBoundary from 'containers/ErrorBoundary';
import { RequireAuthentication } from 'containers/RequireAuthentication';
import SetFlashMessages from 'containers/SetFlashMessages';
import SetGMapsError from 'containers/SetGMapsError';
import { ShowUserModals } from 'containers/ShowUserModals';
import { TabContextProvider } from 'containers/TabContext';
import { UpdateRequiredContextProvider } from 'containers/UpdateRequiredContext';
import { CmsContentPageContainerLoadable } from 'modules/cms/pages/Content';
import { CmsDashboardApiPage } from 'modules/cmsDashboard/api/types/CmsDashboardApiPage';
import { GradProgramPageContainerLoadable } from 'modules/gradSchools/pages/GradProgramPageContainerLoadable';
import { AdminsLinksLoadable } from 'modules/main/pages/AdminsLinks';
import { BuyCreditsLinksLoadable } from 'modules/main/pages/BuyCreditsLinks';
import { CompensationSurveyLoadable } from 'modules/main/pages/CompensationSurvey';
import { CreditsLinksLoadable } from 'modules/main/pages/CreditsLinks';
import { EditOrgLinksLoadable } from 'modules/main/pages/EditOrgLinks';
import { ForgotPasswordPageContainerLoadable } from 'modules/main/pages/ForgotPassword';
import { GradFairsLinksLoadable } from 'modules/main/pages/GradFairsLinks';
import { ImportedListingPageContainerLoadable } from 'modules/main/pages/ImportedListing';
import { IntegrateAtsLinksLoadable } from 'modules/main/pages/IntegrateAtsLinks';
import { InvalidLinkLoadable } from 'modules/main/pages/InvalidLink';
import { InvoiceViewContainerLoadable } from 'modules/main/pages/InvoiceView';
import { InvoicesLinksLoadable } from 'modules/main/pages/InvoicesLinks';
import { ListingPageContainerLoadable } from 'modules/main/pages/Listing';
import { LoginPageContainerLoadable } from 'modules/main/pages/Login';
import { NotificationsLinksLoadable } from 'modules/main/pages/NotificationsLinks';
import { OrgPageContainerLoadable } from 'modules/main/pages/Org';
import { RateRecommendedCandidateLoadable } from 'modules/main/pages/RateRecommendedCandidate';
import { ResetPasswordPageContainerLoadable } from 'modules/main/pages/ResetPassword';
import { SalaryReportLoadable } from 'modules/main/pages/SalaryReport';
import { SearchPageContainerLoadable } from 'modules/main/pages/Search';
import { SignupPageContainerLoadable } from 'modules/main/pages/Signup';
import { UserInviteContainerLoadable } from 'modules/main/pages/UserInvite';
import { OrgDashboardAppLoadable } from 'modules/orgDashboard/OrgDashboardAppLoadable';
import { UserDashboardAppLoadable } from 'modules/userDashboard/UserDashboardAppLoadable';
import { ThemeGlobalStyle } from 'theme';
import { AmpContext } from 'utils/AmpContext';
import { WithRouterParams } from 'utils/WithRouterParams';
import { configValue } from 'utils/config';
import { SIGN_UP_TO_POST_URL_FOR_LOCALE } from 'utils/constants';
import { hasParam } from 'utils/url';

const ORG_TYPE_URL_SEGMENTS = [
  // en
  'nonprofit',
  'government',
  'consultant',
  'social-enterprise',
  'recruiter',
  'corporation', // TODO depreacted, remove after 2021-07-01
  'business', // es
  'ong',
  'gobierno',
  'consultor',
  'empresa-social',
  'agencia-de-contratacion',
  'empresa', // pt
  'ong',
  'governo',
  'consultoria',
  'empreendedorismo-social',
  'recrutador',
  'empresa',
];

const JOB_URL_SEGMENTS = [
  'job', // en
  'empleo', // es
  'emprego', // pt
];

const INTERNSHIP_URL_SEGMENTS = [
  'internship', // en
  'pasantia', // es
  'estagio', // pt
];

const VOLOP_URL_SEGMENTS = [
  'volunteer-opportunity', // en
  'volunter-opportunity', // en deprecated. TODO: remove in April 2021.
  'oportunidad-voluntariado', // es
  'oportunidade-voluntariado', // pt
];

const GRAD_PROGRAM_URL_SEGMENTS = [
  'grad-program', // en
  'programa-posgrado', // es
  'programa-posgraduacao', // pt
];

const ORG_TYPES_RE = `(${ORG_TYPE_URL_SEGMENTS.join('|')})`;
const JOBS_RE = `(${JOB_URL_SEGMENTS.join('|')})`;
const INTERNSHIPS_RE = `(${INTERNSHIP_URL_SEGMENTS.join('|')})`;
const VOLOPS_RE = `(${VOLOP_URL_SEGMENTS.join('|')})`;
const GRAD_PROGRAMS_RE = `(${GRAD_PROGRAM_URL_SEGMENTS.join('|')})`;

type Props = {
  content?: CmsDashboardApiPage;
  requestIsAmp?: boolean;
};

export default function MainApp({ content, requestIsAmp }: Props) {
  return (
    <ErrorBoundary>
      <ThemeGlobalStyle />
      <SetGMapsError />
      <SetFlashMessages />
      <TemporaryStorageHandler />
      <ShowUserModals>
        <AmpContext.Provider value={!!requestIsAmp}>
          <UpdateRequiredContextProvider>
            <TabContextProvider>
              <Switch>
                <Route exact path="/login">
                  <LoginPageContainerLoadable />
                </Route>
                <Route exact path="/signup">
                  <SignupPageContainerLoadable />
                </Route>
                <Route exact path="/forgot-password">
                  <ForgotPasswordPageContainerLoadable />
                </Route>
                <Route exact path="/reset-password/:token">
                  <WithRouterParams
                    render={({ token }) => (
                      <ResetPasswordPageContainerLoadable token={token} />
                    )}
                  />
                </Route>
                <Route exact path="/invalid-or-expired-link">
                  <InvalidLinkLoadable />
                </Route>
                <Route exact path="/salaries/survey">
                  <CompensationSurveyLoadable />
                </Route>
                <Route exact path="/salaries/explore">
                  <SalaryReportLoadable />
                </Route>
                <Route exact path="/invoices/:invoiceId([0-9A-Za-z]{32})">
                  <WithRouterParams
                    render={({ invoiceId }) => (
                      <InvoiceViewContainerLoadable invoiceId={invoiceId} />
                    )}
                  />
                </Route>
                <Route
                  exact
                  path="/donation-receipts/:invoiceId([0-9A-Za-z]{32})"
                >
                  <WithRouterParams
                    render={({ invoiceId }) => (
                      <InvoiceViewContainerLoadable
                        invoiceId={invoiceId}
                        isDonation
                      />
                    )}
                  />
                </Route>
                <Route
                  exact
                  path={`/:lang/:amp(amp)?/${ORG_TYPES_RE}/:orgId-(.*)`}
                >
                  <WithRouterParams
                    render={({ orgId }) => (
                      <OrgPageContainerLoadable orgId={orgId} />
                    )}
                  />
                </Route>
                <Route exact path={`/:lang/:amp(amp)?/${JOBS_RE}/:jobId-(.*)`}>
                  <WithRouterParams
                    render={({ jobId }) => (
                      <ListingPageContainerLoadable
                        listingType="JOB"
                        listingId={jobId}
                      />
                    )}
                  />
                </Route>
                <Route
                  exact
                  path={`/:lang/:amp(amp)?/${ORG_TYPES_RE}-${JOBS_RE}/:jobId-(.*)`}
                >
                  <WithRouterParams
                    render={({ jobId }) => (
                      <ListingPageContainerLoadable
                        listingType="JOB"
                        listingId={jobId}
                      />
                    )}
                  />
                </Route>
                <Route
                  exact
                  path={`/:lang/:amp(amp)?/${INTERNSHIPS_RE}/:internshipId-(.*)`}
                >
                  <WithRouterParams
                    render={({ internshipId }) => (
                      <ListingPageContainerLoadable
                        listingType="INTERNSHIP"
                        listingId={internshipId}
                      />
                    )}
                  />
                </Route>
                <Route
                  exact
                  path={`/:lang/:amp(amp)?/${ORG_TYPES_RE}-${INTERNSHIPS_RE}/:internshipId-(.*)`}
                >
                  <WithRouterParams
                    render={({ internshipId }) => (
                      <ListingPageContainerLoadable
                        listingType="INTERNSHIP"
                        listingId={internshipId}
                      />
                    )}
                  />
                </Route>
                <Route
                  exact
                  path={`/:lang/:amp(amp)?/${VOLOPS_RE}/:volopId-(.*)`}
                >
                  <WithRouterParams
                    render={({ volopId }) => (
                      <ListingPageContainerLoadable
                        listingType="VOLOP"
                        listingId={volopId}
                      />
                    )}
                  />
                </Route>
                <Route
                  exact
                  path="/:lang/imported-action-opps/:source/:volopId"
                >
                  <WithRouterParams
                    render={({ volopId, source }) => (
                      <ImportedListingPageContainerLoadable
                        // @ts-expect-error fix type
                        source={source}
                        listingId={volopId}
                      />
                    )}
                  />
                </Route>
                <Route exact path="/user-invites/:inviteId">
                  <WithRouterParams
                    render={({ inviteId }) => (
                      <UserInviteContainerLoadable inviteId={inviteId} />
                    )}
                  />
                </Route>
                <Route
                  path="/dashboard"
                  render={() => (
                    <RequireAuthentication>
                      <UserDashboardAppLoadable />
                    </RequireAuthentication>
                  )}
                />
                <Route
                  path="/organizations/create"
                  render={() => (
                    <RequireAuthentication
                      loginPathname={
                        SIGN_UP_TO_POST_URL_FOR_LOCALE[CURRENT_LOCALE]
                      }
                    >
                      <UserDashboardAppLoadable />
                    </RequireAuthentication>
                  )}
                />
                {configValue('idealist', 'gsdEnabled') === 'true' && (
                  <Route
                    path="/organizations/create-with-grad-program"
                    render={() => (
                      <RequireAuthentication>
                        <UserDashboardAppLoadable />
                      </RequireAuthentication>
                    )}
                  />
                )}
                <Route
                  path="/admin/:orgId"
                  render={() => (
                    <RequireAuthentication>
                      <WithRouterParams
                        render={({ orgId }) => (
                          <OrgDashboardAppLoadable orgId={orgId} />
                        )}
                      />
                    </RequireAuthentication>
                  )}
                />
                <Route
                  path="/post-a-listing"
                  render={() => (
                    <RequireAuthentication
                      loginPathname={
                        SIGN_UP_TO_POST_URL_FOR_LOCALE[CURRENT_LOCALE]
                      }
                    >
                      <UserDashboardAppLoadable />
                    </RequireAuthentication>
                  )}
                />
                <Route
                  path="/select-org"
                  render={() => (
                    <RequireAuthentication>
                      <UserDashboardAppLoadable />
                    </RequireAuthentication>
                  )}
                />
                <Route
                  path="/manage-grad-program"
                  render={() => (
                    <RequireAuthentication>
                      <UserDashboardAppLoadable />
                    </RequireAuthentication>
                  )}
                />
                <Route
                  exact
                  path="/buy-credits"
                  render={() => (
                    <RequireAuthentication>
                      <BuyCreditsLinksLoadable />
                    </RequireAuthentication>
                  )}
                />
                <Route
                  exact
                  path="/integrate-ats"
                  render={() => (
                    <RequireAuthentication>
                      <IntegrateAtsLinksLoadable />
                    </RequireAuthentication>
                  )}
                />
                <Route
                  exact
                  path="/notifications"
                  render={() => (
                    <RequireAuthentication>
                      <NotificationsLinksLoadable />
                    </RequireAuthentication>
                  )}
                />
                <Route
                  exact
                  path="/credits"
                  render={() => (
                    <RequireAuthentication>
                      <CreditsLinksLoadable />
                    </RequireAuthentication>
                  )}
                />
                <Route
                  exact
                  path="/admins"
                  render={() => (
                    <RequireAuthentication>
                      <AdminsLinksLoadable />
                    </RequireAuthentication>
                  )}
                />
                <Route
                  exact
                  path="/edit-org"
                  render={() => (
                    <RequireAuthentication>
                      <EditOrgLinksLoadable />
                    </RequireAuthentication>
                  )}
                />
                <Route
                  exact
                  path="/gradfairs"
                  render={() => (
                    <RequireAuthentication>
                      <GradFairsLinksLoadable />
                    </RequireAuthentication>
                  )}
                />
                <Route
                  exact
                  path="/invoices"
                  render={() => (
                    <RequireAuthentication>
                      <InvoicesLinksLoadable />
                    </RequireAuthentication>
                  )}
                />
                <Route
                  exact
                  path="/email/rate-recommended-candidate/:applicantId/good"
                >
                  <RequireAuthentication>
                    <WithRouterParams
                      render={({ applicantId }) => (
                        <RateRecommendedCandidateLoadable
                          applicantId={applicantId}
                          isGood
                        />
                      )}
                    />
                  </RequireAuthentication>
                </Route>
                <Route
                  exact
                  path="/email/rate-recommended-candidate/:applicantId/bad"
                >
                  <RequireAuthentication>
                    <WithRouterParams
                      render={({ applicantId }) => (
                        <RateRecommendedCandidateLoadable
                          applicantId={applicantId}
                          isGood={false}
                        />
                      )}
                    />
                  </RequireAuthentication>
                </Route>
                <Route
                  exact
                  path={`/:lang/:amp(amp)?/${GRAD_PROGRAMS_RE}/:programId-(.*)`}
                >
                  <WithRouterParams
                    render={({ programId }) => (
                      <GradProgramPageContainerLoadable programId={programId} />
                    )}
                  />
                </Route>
                <Route path="/:amp(amp)?/:slug*">
                  <WithRouterParams
                    render={({ slug, amp }, location) => {
                      const isLandingPage =
                        (content &&
                          ['CMS_SEARCH_LANDING_PAGE', 'LANDING_PAGE'].includes(
                            content.type
                          )) ||
                        false;

                      const {
                        redirectToContent = false,
                      }: { redirectToContent?: boolean } = location.state || {};

                      if (
                        hasParam(location.search, 'q') ||
                        hasParam(location.search, 'pq') ||
                        isLandingPage
                      ) {
                        if (redirectToContent) {
                          return (
                            <CmsContentPageContainerLoadable
                              slug={slug}
                              amp={!!amp}
                            />
                          );
                        }
                        return (
                          <SearchPageContainerLoadable
                            slug={slug}
                            amp={!!amp}
                          />
                        );
                      }

                      return (
                        <CmsContentPageContainerLoadable
                          slug={slug}
                          amp={!!amp}
                        />
                      );
                    }}
                  />
                </Route>
              </Switch>
            </TabContextProvider>
          </UpdateRequiredContextProvider>
        </AmpContext.Provider>
      </ShowUserModals>
    </ErrorBoundary>
  );
}
